import { Box, Typography, Chip, useTheme, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SaveButton, DiscardButton } from '@components/common';

interface AllowedEventsHeaderProps {
  selectedEvent: string | null;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}

export const AllowedEventsHeader = ({
  selectedEvent,
  hasUnsavedChanges,
  isSaving,
  onSave,
  onDiscard
}: AllowedEventsHeaderProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: { xs: 2, sm: 3, md: 4 },
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        position: 'sticky',
        top: 0,
        zIndex: 1
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 600,
            color: theme.palette.primary.main
          }}
        >
          {t('settings:allowedEvents.title')}
        </Typography>
        <Chip
          label={
            selectedEvent ?? t('settings:allowedEvents.header.noneSelected')
          }
          size="small"
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 500
          }}
        />
      </Box>

      {hasUnsavedChanges && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <DiscardButton
            onClick={onDiscard}
            disabled={isSaving}
            sx={{ minWidth: 'auto' }}
          >
            {t('settings:allowedEvents.header.discard')}
          </DiscardButton>
          <SaveButton
            onClick={onSave}
            loading={isSaving}
            disabled={isSaving}
            sx={{ minWidth: 'auto' }}
          >
            {isSaving
              ? t('settings:allowedEvents.header.saving')
              : t('settings:allowedEvents.header.save')}
          </SaveButton>
        </Box>
      )}
    </Box>
  );
};
